import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Mail, Send, Trash2, ShieldCheck, ShieldX, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { Modal, Field } from "../pages/crm/crmui";
import { Btn, Badge } from "./common";

export default function PortalAccessManager({ account }) {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [novo, setNovo] = useState({ nome: "", email: "", password: "", enviar_convite: true });
  const [loading, setLoading] = useState(false);

  const canManage = account?.tipo === "cliente";

  const load = async () => {
    if (!account?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/portal-admin/users/${account.id}`);
      setUsers(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [account?.id]);

  const criar = async () => {
    if (!novo.nome.trim() || !novo.email.trim()) return toast.error("Indique nome e email.");
    try {
      const { data } = await api.post(`/portal-admin/users`, { ...novo, account_id: account.id });
      toast.success("Acesso criado.");
      if (data.link_convite) {
        try { await navigator.clipboard.writeText(data.link_convite); } catch {}
        toast.info("Link de convite copiado.");
      }
      setShowAdd(false);
      setNovo({ nome: "", email: "", password: "", enviar_convite: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    try {
      await api.put(`/portal-admin/users/${u.id}`, { ativo: !u.ativo });
      toast.success(u.ativo ? "Acesso desativado." : "Acesso ativado.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reenviar = async (u) => {
    try {
      const { data } = await api.post(`/portal-admin/users/${u.id}/resend-invite`);
      if (data.link_convite) {
        try { await navigator.clipboard.writeText(data.link_convite); } catch {}
      }
      toast.success(data.convite_enviado ? "Convite reenviado por email." : "Link copiado (email nao configurado).");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remover = async (u) => {
    if (!confirm(`Remover acesso de ${u.email}?`)) return;
    try {
      await api.delete(`/portal-admin/users/${u.id}`);
      toast.success("Acesso removido.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="portal-access-manager">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] font-semibold text-slate-700">
          {users.length} utilizador(es) do portal
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          disabled={!canManage}
          data-testid="portal-add-user-btn"
          title={canManage ? "Adicionar acesso" : "So contas do tipo cliente podem ter acesso ao portal"}
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded bg-blue text-white hover:bg-[var(--blue-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <UserPlus size={12} /> Convidar
        </button>
      </div>

      {loading && <div className="text-[11px] text-slate-400 italic">A carregar...</div>}

      {!loading && users.length === 0 && (
        <div className="text-[12px] text-slate-400 italic">
          Sem acessos configurados. {!canManage && "Converta primeiro em cliente."}
        </div>
      )}

      <div className="space-y-1.5">
        {users.map((u) => (
          <div key={u.id} data-testid={`portal-user-${u.id}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
            <div className="w-7 h-7 rounded-full bg-blue/15 text-blue text-[10px] font-bold flex items-center justify-center shrink-0">
              {(u.nome || u.email || "?").split(" ").slice(0, 2).map((x) => x[0] || "").join("").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-navy truncate flex items-center gap-2">
                {u.nome || u.email}
                {u.invite_token && <span className="text-[9px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">convite pendente</span>}
                {!u.ativo && <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200">inativo</span>}
              </div>
              <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                <Mail size={10} /> {u.email}
              </div>
            </div>
            <button onClick={() => reenviar(u)} title="Reenviar convite" data-testid={`portal-user-resend-${u.id}`}
              className="p-1 rounded text-slate-500 hover:text-blue hover:bg-slate-50">
              <Send size={13} />
            </button>
            <button onClick={() => toggle(u)} title={u.ativo ? "Desativar" : "Ativar"} data-testid={`portal-user-toggle-${u.id}`}
              className="p-1 rounded text-slate-500 hover:text-navy hover:bg-slate-50">
              {u.ativo ? <ShieldX size={13} /> : <ShieldCheck size={13} />}
            </button>
            <button onClick={() => remover(u)} title="Remover" data-testid={`portal-user-delete-${u.id}`}
              className="p-1 rounded text-slate-500 hover:text-red-600 hover:bg-red-50">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Convidar acesso ao portal" testid="portal-invite-modal">
        <p className="text-xs text-muted2">O utilizador recebe um email com link para definir a sua password (valido 7 dias). Se nao houver SendGrid configurado, o link e copiado para a area de transferencia.</p>
        <Field label="Nome" value={novo.nome} onChange={(v) => setNovo({ ...novo, nome: v })} testid="portal-invite-nome" />
        <Field label="Email" value={novo.email} onChange={(v) => setNovo({ ...novo, email: v })} testid="portal-invite-email" />
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!novo.enviar_convite} onChange={(e) => setNovo({ ...novo, enviar_convite: e.target.checked })} data-testid="portal-invite-toggle" />
          Enviar convite (o utilizador define a password)
        </label>
        {!novo.enviar_convite && (
          <Field label="Password inicial (min. 8)" type="password" value={novo.password} onChange={(v) => setNovo({ ...novo, password: v })} testid="portal-invite-password" />
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={() => setShowAdd(false)}>Cancelar</Btn>
          <Btn onClick={criar} testid="portal-invite-save">Criar acesso</Btn>
        </div>
      </Modal>
    </div>
  );
}
